export type ChatRole = 'user' | 'assistant'

export interface StoredToolCall {
  name: string
}

/**
 * Lightweight manifest of a sent attachment kept locally so the user can
 * still see "I sent screenshot.png" after a reload.
 *
 * `dataUrl` (data:mime;base64,...) is held in memory ONLY — it lets the
 * chat surface show a real preview of what the user just sent. It is
 * deliberately stripped before persistence by `saveMessages` because a
 * single 5MB image inflates to ~7MB of base64 and would blow the
 * localStorage quota within a handful of messages.
 */
export interface StoredAttachment {
  name: string
  mime: string
  size: number
  /** In-memory only. Cleared on persist. */
  dataUrl?: string
}

export interface StoredMessage {
  role: ChatRole
  content: string
  toolCalls?: StoredToolCall[]
  attachments?: StoredAttachment[]
}

const SESSION_KEY = 'stark.chat.currentSessionId'
const messagesKey = (sessionId: string) => `stark.chat.messages.${sessionId}`

export function loadCurrentSessionId(): string | null {
  try {
    return localStorage.getItem(SESSION_KEY)
  } catch {
    return null
  }
}

export function saveCurrentSessionId(sessionId: string | null): void {
  try {
    if (sessionId === null) localStorage.removeItem(SESSION_KEY)
    else localStorage.setItem(SESSION_KEY, sessionId)
  } catch {
    // localStorage can throw in private mode or when full; chat still works in-memory.
  }
}

export function loadMessages(sessionId: string | null): StoredMessage[] {
  if (!sessionId) return []
  try {
    const raw = localStorage.getItem(messagesKey(sessionId))
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isStoredMessage)
  } catch {
    return []
  }
}

export function saveMessages(sessionId: string, messages: StoredMessage[]): void {
  try {
    // Strip BASE64 data URLs before persisting so a few image messages don't
    // fill the localStorage quota — but KEEP short server URLs (/api/uploads/...)
    // since they cost almost nothing and let the preview reload after a refresh.
    const sanitized = messages.map((m) => {
      if (!m.attachments || m.attachments.length === 0) return m
      return {
        ...m,
        attachments: m.attachments.map((a) => {
          const keepUrl = a.dataUrl && !a.dataUrl.startsWith('data:')
          return {
            name: a.name,
            mime: a.mime,
            size: a.size,
            ...(keepUrl ? { dataUrl: a.dataUrl } : {}),
          }
        }),
      }
    })
    localStorage.setItem(messagesKey(sessionId), JSON.stringify(sanitized))
  } catch {
    // ignore quota errors; session continues in memory
  }
}

export function clearMessages(sessionId: string | null): void {
  if (!sessionId) return
  try {
    localStorage.removeItem(messagesKey(sessionId))
  } catch {
    // ignore
  }
}

function isStoredMessage(value: unknown): value is StoredMessage {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    (v['role'] === 'user' || v['role'] === 'assistant') &&
    typeof v['content'] === 'string'
  )
}
